import { useMutation, gql, useLazyQuery } from '@apollo/client'
import {
  RetailerAccount,
  Location,
  Query,
  Mutation,
} from '@promoboxx/graphql-gateway-types'
import { useEffect, useMemo, useCallback, useState } from 'react'

///////////////////////////////////////////////////////////////////////////////
// LOCATIONS
///////////////////////////////////////////////////////////////////////////////
const LOCATION_FRAGMENT = gql`
  fragment LocationFragment on Location {
    id
    addr1
    addr2
    city
    state
    zip
    latitude
    longitude
    created_at
    updated_at
    website
    phone
    phone_extension
    country_code
    created_by_id
    updated_by_id
    deleted_at
    locationable_type
    locationable_id
    google_place_reference
    google_place_id
    parent_id
    locationable {
      ... on Retailer {
        id
        name
        slug
        logo_url
      }
      ... on RetailerAccount {
        id
        name
        slug
        logo_url
      }
      ... on LocalAds {
        id
      }
    }
    children {
      id
      addr1
      addr2
      city
      state
      zip
      latitude
      longitude
      created_at
      updated_at
      website
      phone
      phone_extension
      country_code
      created_by_id
      updated_by_id
      deleted_at
      locationable_type
      locationable_id
      google_place_reference
      google_place_id
      parent_id
    }
  }
`

const GET_LOCATIONS = gql`
  ${LOCATION_FRAGMENT}
  query getLocationsUseLocations(
    $resourceType: LocationResourceType!
    $resourceId: ID!
    $fields: [LocationFields!]
  ) {
    getLocations(
      resourceType: $resourceType
      resourceId: $resourceId
      fields: $fields
    ) {
      ...LocationFragment
    }
  }
`

// const GET_LOCATION = gql`
//   ${LOCATION_FRAGMENT}
//   query getLocationUseLocations(
//     $id: ID!
//     $fields: [LocationFields]
//   ) {
//     getLocation(
//       id: $id
//       fields: $fields
//     ) {
//       ...LocationFragment
//     }
//   }
// `

const CREATE_LOCATION = gql`
  ${LOCATION_FRAGMENT}
  mutation createLocationUseLocations(
    $resourceType: LocationResourceType!
    $resourceId: ID!
    $location: LocationInput!
  ) {
    createLocation(
      resourceType: $resourceType
      resourceId: $resourceId
      location: $location
    ) {
      ...LocationFragment
    }
  }
`

const UPDATE_LOCATION = gql`
  ${LOCATION_FRAGMENT}
  mutation updateLocationUseLocations($id: ID!, $location: LocationInput!) {
    updateLocation(id: $id, location: $location) {
      ...LocationFragment
    }
  }
`

const DELETE_LOCATION = gql`
  mutation deleteLocationUseLocations($id: ID!) {
    deleteLocation(id: $id)
  }
`

export function useLocations(retailerAccount: RetailerAccount) {
  const [locations, setLocations] = useState<Location[] | null>(null)
  const [errors, setErrors] = useState<Error[] | unknown[]>([])

  const refetchLocations = useMemo(
    () => [
      {
        query: GET_LOCATIONS,
        variables: {
          resourceType: 'retailer_accounts',
          resourceId: retailerAccount.id,
          fields: ['locationable', 'children'],
        },
      },
    ],
    [retailerAccount],
  )

  const [getLocationsQuery, getLocationsResults] =
    useLazyQuery<Query>(GET_LOCATIONS)

  const [createLocationMutation] = useMutation<Mutation>(CREATE_LOCATION)
  const [updateLocationMutation] = useMutation<Mutation>(UPDATE_LOCATION)
  const [deleteLocationMutation] = useMutation<Mutation>(DELETE_LOCATION)

  useEffect(() => {
    setLocations((getLocationsResults?.data?.getLocations as Location[]) || [])
  }, [getLocationsResults.data])
  // useEffect(() => {
  //   if (getLocationResults.data && getLocationResults.data.getLocation) {
  //     setLocations(getLocationResults.data.getLocation)
  //   }
  // }, [getLocationResults.data])

  const getLocations = useCallback(async () => {
    return getLocationsQuery({
      variables: {
        resourceType: 'retailer_accounts',
        resourceId: retailerAccount.id,
        fields: ['locationable', 'children'],
      },
    })
      .then((response) => {
        setLocations((response?.data?.getLocations as Location[]) || [])
      })
      .catch((error) => {
        setErrors([...(errors || []), error])
      })
  }, [retailerAccount, getLocationsQuery, errors])

  // const getLocation = (id) => {
  //   return getLocationQuery({
  //     variables: {
  //       id,
  //       fields: ['locationable', 'children'],
  //     },
  //   })
  //     .then((response) => {
  //       if (response && response.data && response.data.getLocation) {
  //         setLocations(response.data.getLocation)
  //       }
  //     })
  //     .catch((error) => {
  //       setErrors([...(errors || []), error])
  //     })
  // }

  const createLocation = useCallback(
    async (id: string, location: Location) => {
      return createLocationMutation({
        variables: {
          id,
          location,
        },
        refetchQueries: refetchLocations,
      }).catch((error) => {
        setErrors([...(errors || []), error])
      })
    },
    [createLocationMutation, errors, refetchLocations],
  )

  const updateLocation = useCallback(
    async (id: string, location: Location) => {
      return updateLocationMutation({
        variables: {
          id,
          location,
        },
        refetchQueries: refetchLocations,
      }).catch((error) => {
        setErrors([...(errors || []), error])
      })
    },
    [updateLocationMutation, errors, refetchLocations],
  )

  const deleteLocation = useCallback(
    async (id: string) => {
      return deleteLocationMutation({
        variables: {
          id,
        },
        refetchQueries: refetchLocations,
      }).catch((error) => {
        setErrors([...(errors || []), error])
      })
    },
    [deleteLocationMutation, errors, refetchLocations],
  )

  return {
    getLocations,
    // getLocation,
    createLocation,
    updateLocation,
    deleteLocation,
    locations,
  }
}
